import { CreatePadRequest, CreatePadNameType } from '../models/CreatePad';
import { AddMessageRequest } from '../models/AddMessage';
import {
  ConfirmFileUploadRequest,
  RequestFileUploadRequest,
  RequestFileUploadResponse,
} from '../models/AddFile';

export const MAX_FILE_BYTES = 25 * 1024 * 1024
export const MAX_PAD_FILE_BYTES = 100 * 1024 * 1024

export const fetchFromApi = async (
  path: string,
  options: any = {},
  password: string = "",
): Promise<any> => {
  try {
    // Seems we need CORS even in production due to subdomain difference.
    options.mode = 'cors'
    options.headers = options.headers || {}
    // Optionally add the password header.
    if (password) {
      options.headers['sd-password'] = password
    }

    const response = await fetch(`https://api.selfdestruct.me/${path}`, options)
    if (!response.ok) {
      throw Error(await response.text())
    }
    return await response.json()
  } catch (error) {
    console.log(error)
    throw error
  }
}

export const createPad = (
  expirationInMinutes: number,
  password: string,
  initialMessage: string
): Promise<any> => {
  const data: CreatePadRequest = {
    name_type: CreatePadNameType.RandomChars,
    expiration_in_minutes: expirationInMinutes
  }
  if (password) {
    data.password = password
  }
  if (initialMessage.trim()) {
    data.initial_message = initialMessage
  }
  const options = {
    method: 'POST',
    body: JSON.stringify(data)
  }
  return fetchFromApi('pad', options)
}

export const getPad = (
  name: string,
  password: string
): Promise<any> => {
  return fetchFromApi(`pad/${name}`, {}, password)
}

export const addMessage = (
  name: string,
  message: string,
  password: string
): Promise<any> => {
  const data: AddMessageRequest = {
    message: message
  }
  const options = {
    method: 'POST',
    body: JSON.stringify(data)
  }
  return fetchFromApi(`pad/${name}/message`, options, password)
}

export const requestFileUploadUrl = (
  name: string,
  fileName: string,
  size: number,
  password: string
): Promise<RequestFileUploadResponse> => {
  const data: RequestFileUploadRequest = {
    name: fileName,
    size,
  }
  const options = {
    method: 'POST',
    body: JSON.stringify(data)
  }
  return fetchFromApi(`pad/${name}/file/upload-url`, options, password)
}

export const confirmFileUpload = (
  name: string,
  objectKey: string,
  fileName: string,
  size: number,
  password: string
): Promise<any> => {
  const data: ConfirmFileUploadRequest = {
    object_key: objectKey,
    name: fileName,
    size,
  }
  const options = {
    method: 'POST',
    body: JSON.stringify(data)
  }
  return fetchFromApi(`pad/${name}/file`, options, password)
}

export const validateFileForUpload = (file: File): string | null => {
  if (file.size <= 0) {
    return "File is empty"
  }
  if (file.size > MAX_FILE_BYTES) {
    return "Files must be under 25 MB"
  }
  return null
}

const putFileWithProgress = (
  uploadUrl: string,
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open("PUT", uploadUrl)
    xhr.setRequestHeader("Content-Type", "application/octet-stream")
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(event.loaded / event.total)
      }
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(Error("Failed to upload file"))
      }
    }
    xhr.onerror = () => reject(Error("Failed to upload file"))
    xhr.onabort = () => reject(Error("Upload aborted"))
    xhr.send(file)
  })
}

export const uploadPadFile = async (
  padName: string,
  file: File,
  password: string,
  onProgress?: (fraction: number) => void,
): Promise<any> => {
  const validationError = validateFileForUpload(file)
  if (validationError) {
    throw Error(validationError)
  }

  const prepared = await requestFileUploadUrl(
    padName, file.name, file.size, password)
  if ((prepared as any).error_code) {
    return prepared
  }

  await putFileWithProgress(prepared.upload_url, file, onProgress)

  return confirmFileUpload(
    padName, prepared.object_key, file.name, file.size, password)
}

export type UploadProgressUpdate = {
  /** Overall 0–100 across all files in this batch */
  percent: number
  /** 1-based index of the file currently uploading */
  current: number
  total: number
  fileName: string
}

export const uploadPadFiles = async (
  padName: string,
  files: File[],
  password: string,
  onProgress?: (update: UploadProgressUpdate) => void,
): Promise<any> => {
  if (files.length === 0) {
    throw Error("No files selected")
  }
  const totalBytes = files.reduce((sum, f) => sum + f.size, 0)
  if (totalBytes > MAX_PAD_FILE_BYTES) {
    throw Error("Files must total under 100 MB per pad")
  }

  let completedBytes = 0
  let lastResponse: any = null

  for (let i = 0; i < files.length; i++) {
    const file = files[i]
    const bytesBefore = completedBytes
    lastResponse = await uploadPadFile(
      padName,
      file,
      password,
      (fraction) => {
        if (!onProgress) {
          return
        }
        const sent = bytesBefore + file.size * fraction
        onProgress({
          percent: totalBytes === 0 ? 100 : (sent / totalBytes) * 100,
          current: i + 1,
          total: files.length,
          fileName: file.name,
        })
      },
    )
    if (lastResponse && lastResponse.error_code) {
      return lastResponse
    }
    completedBytes += file.size
    if (onProgress) {
      onProgress({
        percent: totalBytes === 0 ? 100 : (completedBytes / totalBytes) * 100,
        current: i + 1,
        total: files.length,
        fileName: file.name,
      })
    }
  }

  return lastResponse
}

export const doesPadExist = (name: string): Promise<any> => {
  return fetchFromApi(`pad/${name}/exists`)
}
